import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'
import { parseConditions, parseRequiredProducts } from '@/components/rewards/utils/offerRewardHydration'
import { getProductKey } from '@/utils/productHelpers'

import type { RewardConditionPayload } from '@/components/rewards/utils/offerRewardPayload'
import type { AccessCondition } from '@/types/offer'
import type { LoyaltyReward, PartnerProduct, RewardConditionOperator, RewardConditionProductsItem } from '@/types/types'

/** Reads `cart_minimum_in_cents` (API/store: cents) and returns euros for the form. */
export function getCartMinimumAmountEuros(reward: LoyaltyReward | null | undefined): number | null {
  const condition = (reward?.conditions ?? []).find((c) => c.type === 'cart_minimum_in_cents')
  if (condition?.value == null || condition.value <= 0) return null
  return condition.value / 100
}

/** Writes euros from the form into `cart_minimum_in_cents` (stored in cents, ×100). */
export function setCartMinimumAmountEuros(reward: LoyaltyReward, euros: number | null): void {
  const otherConditions = (reward.conditions ?? []).filter((c) => c.type !== 'cart_minimum_in_cents')
  if (euros != null && euros > 0) {
    reward.conditions = [
      ...otherConditions,
      { type: 'cart_minimum_in_cents', operator: 'gte', value: Math.round(euros * 100) }
    ]
  } else {
    reward.conditions = otherConditions
  }
}

export function getAccessCondition(reward: LoyaltyReward | null | undefined): AccessCondition {
  if (!reward) return 'none'
  if (slugBelongsTo('freeDelivery', reward.rewardType?.slug)) return 'none'
  if (reward.accessCondition) return reward.accessCondition
  const cartMinEuros = getCartMinimumAmountEuros(reward)
  return cartMinEuros != null && cartMinEuros > 0 ? 'min_amount' : 'none'
}

export interface AccessConditionsLocalState {
  requiredProducts: PartnerProduct[]
  requiredProductsOperator: RewardConditionOperator
  requiredProductQuantities: Record<string, number>
}

export function hydrateAccessConditionsFromReward(reward: LoyaltyReward): AccessConditionsLocalState {
  if (slugBelongsTo('xForY', reward.rewardType?.slug)) {
    return {
      requiredProducts: [],
      requiredProductsOperator: 'contains_all',
      requiredProductQuantities: {}
    }
  }

  let requiredProducts = parseRequiredProducts(reward)
  let requiredProductsOperator: RewardConditionOperator = 'contains_all'
  let requiredProductQuantities: Record<string, number> = {}

  const conditions = (reward.conditions ?? []) as RewardConditionPayload[]
  const parsed = parseConditions(conditions)

  if (parsed.requiredProducts.length > 0) {
    requiredProductsOperator = parsed.requiredProductsOperator
    requiredProducts = parsed.requiredProducts
    requiredProductQuantities = parsed.requiredProductQuantities
    reward.requiredProductExternalIds = parsed.requiredProducts.map((p) => p.externalId.toString())
    reward.accessCondition = 'products_required'
    setCartMinimumAmountEuros(reward, null)
    reward.cartItemCount = null
  } else if (parsed.cartItemCount != null && parsed.cartItemCount > 0) {
    reward.cartItemCount = parsed.cartItemCount
    reward.accessCondition = 'cart_item_count'
    setCartMinimumAmountEuros(reward, null)
    requiredProducts = []
  } else if (parsed.accessCondition === 'min_amount') {
    reward.accessCondition = 'min_amount'
    reward.cartItemCount = null
    requiredProducts = []
  }

  if (slugBelongsTo('freeDelivery', reward.rewardType?.slug)) {
    reward.accessCondition = 'none'
    reward.cartItemCount = null
    requiredProducts = []
  }

  return { requiredProducts, requiredProductsOperator, requiredProductQuantities }
}

export function createEmptyAccessConditionsState(): AccessConditionsLocalState {
  return {
    requiredProducts: [],
    requiredProductsOperator: 'contains_all',
    requiredProductQuantities: {}
  }
}

export function syncRequiredProductsToReward(
  reward: LoyaltyReward,
  requiredProducts: PartnerProduct[],
  requiredProductQuantities: Record<string, number>,
  requiredProductsOperator: RewardConditionOperator
): void {
  const requiredProductExternalIds = requiredProducts.map((p) => p.externalId.toString())
  reward.requiredProductExternalIds = requiredProductExternalIds

  const otherConditions = (reward.conditions ?? []).filter((c) => c.type !== 'required_products')
  const ids = requiredProductExternalIds.filter((id) => id.length > 0)

  if (ids.length > 0) {
    const items: RewardConditionProductsItem[] = ids.map((externalId) => {
      const catalogProduct = requiredProducts.find((p) => p.externalId.toString() === externalId)
      const key = catalogProduct ? getProductKey(catalogProduct) : `manual-${externalId}`
      return { externalId, quantity: requiredProductQuantities[key] ?? 1 }
    })
    reward.conditions = [
      ...otherConditions,
      {
        type: 'required_products',
        operator: requiredProductsOperator,
        products: { items }
      }
    ]
  } else {
    reward.conditions = otherConditions
  }
}

export function hasRequiredProductsAccessError(
  reward: LoyaltyReward | null | undefined,
  requiredProducts: PartnerProduct[]
): boolean {
  if (!reward || getAccessCondition(reward) !== 'products_required') return false
  return requiredProducts.length === 0
}
